import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSpace } from '../../contexts/SpaceContext';
import { API_ENDPOINTS } from '../../config';
import '../../styles/components/manageCollectionsModal.css';

interface Collection {
  id: number;
  name: string;
  is_system: boolean;
  is_checklist?: boolean;
  stock_keeping?: boolean;
  icon?: string;
  color?: string;
  item_ids: number[];
  count: number;
}

interface CollectionMembershipState {
  collectionId: number;
  collectionName: string;
  isSystem: boolean;
  currentCount: number;
  totalCount: number;
  state: 'all' | 'none' | 'some';
  pendingAction: 'add' | 'remove' | null;
  itemsToAdd: number[];
  itemsToRemove: number[];
}

interface ManageCollectionsModalProps {
  selectedItemIds: number[];
  onClose: () => void;
  onSuccess: () => void;
}

const ManageCollectionsModal: React.FC<ManageCollectionsModalProps> = ({
  selectedItemIds,
  onClose,
  onSuccess
}) => {
  const { token } = useAuth();
  const { activeSpace } = useSpace();
  const [collections, setCollections] = useState<CollectionMembershipState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCollectionMembership();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch once on mount

  const fetchCollectionMembership = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      
      const spaceParam = activeSpace?.id ? `&space_id=${activeSpace.id}` : '';
      const response = await fetch(
        `${API_ENDPOINTS.GET_COLLECTION_MEMBERSHIP(selectedItemIds)}${spaceParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch collection membership');
      }

      const data = await response.json();
      
      const membershipStates: CollectionMembershipState[] = data.collections.map((col: Collection) => {
        const currentCount = col.count;
        const totalCount = selectedItemIds.length;
        
        let state: 'all' | 'none' | 'some';
        if (currentCount === 0) state = 'none';
        else if (currentCount === totalCount) state = 'all';
        else state = 'some';

        return {
          collectionId: col.id,
          collectionName: col.name,
          isSystem: col.is_system,
          currentCount,
          totalCount,
          state,
          pendingAction: null,
          itemsToAdd: [],
          itemsToRemove: []
        };
      });

      setCollections(membershipStates);
    } catch (err) {
      console.error('Error fetching collection membership:', err);
      setError('Failed to load collections. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxClick = async (index: number) => {
    const collection = collections[index];
    let updatedCollection = { ...collection };
    
    switch (collection.state) {
      case 'all':
        updatedCollection.pendingAction = 'remove';
        updatedCollection.itemsToRemove = selectedItemIds;
        updatedCollection.itemsToAdd = [];
        updatedCollection.state = 'none';
        updatedCollection.currentCount = 0;
        break;
        
      case 'none':
        updatedCollection.pendingAction = 'add';
        updatedCollection.itemsToAdd = selectedItemIds;
        updatedCollection.itemsToRemove = [];
        updatedCollection.state = 'all';
        updatedCollection.currentCount = selectedItemIds.length;
        break;
        
      case 'some':
        try {
          if (!token) return;
          const spaceParam = activeSpace?.id ? `&space_id=${activeSpace.id}` : '';
          const response = await fetch(
            `${API_ENDPOINTS.GET_COLLECTION_MEMBERSHIP(selectedItemIds)}${spaceParam}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          const data = await response.json();
          const currentCollection = data.collections.find((c: Collection) => c.id === collection.collectionId);
          const itemsInCollection = currentCollection?.item_ids || [];
          const itemsToAdd = selectedItemIds.filter(id => !itemsInCollection.includes(id));
          
          updatedCollection.pendingAction = 'add';
          updatedCollection.itemsToAdd = itemsToAdd;
          updatedCollection.itemsToRemove = [];
          updatedCollection.state = 'all';
          updatedCollection.currentCount = selectedItemIds.length;
        } catch (err) {
          console.error('Error fetching collection details:', err);
          return;
        }
        break;
    }
    
    setCollections(prev => {
      const updated = [...prev];
      updated[index] = updatedCollection;
      return updated;
    });
  };

  const handleApplyChanges = async () => {
    try {
      setSaving(true);
      setError(null);

      const changes = collections
        .filter(col => col.pendingAction !== null)
        .map(col => ({
          collection_id: col.collectionId,
          action: col.pendingAction!,
          item_ids: col.pendingAction === 'add' ? col.itemsToAdd : col.itemsToRemove
        }));

      if (changes.length === 0) {
        onClose();
        return;
      }

      if (!token) {
        setError('Not authenticated');
        setSaving(false);
        return;
      }

      const response = await fetch(API_ENDPOINTS.BATCH_UPDATE_COLLECTIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ changes })
      });

      if (!response.ok) {
        throw new Error('Failed to update collections');
      }

      const result = await response.json();
      
      if (result.errors && result.errors.length > 0) {
        setError(`Some changes failed: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Error applying changes:', err);
      setError('Failed to apply changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getCheckboxIcon = (state: 'all' | 'none' | 'some') => {
    switch (state) {
      case 'all':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="2" width="16" height="16" rx="4" fill="var(--color-accent)" />
            <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        );
      case 'none':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="2" width="16" height="16" rx="4" stroke="#8E8E93" strokeWidth="2" fill="white" />
          </svg>
        );
      case 'some':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="2" y="2" width="16" height="16" rx="4" fill="var(--color-accent)" />
            <rect x="6" y="9" width="8" height="2" rx="1" fill="white" />
          </svg>
        );
    }
  };

  const hasPendingChanges = collections.some(col => col.pendingAction !== null);

  return createPortal(
    <div className="mcm-overlay" onClick={onClose}>
      <div className="mcm-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="mcm-header">
          <h2>Add to collections ({selectedItemIds.length} {selectedItemIds.length === 1 ? 'item' : 'items'})</h2>
          <button className="mcm-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M18 6L6 18M6 6l12 12" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Scrollable Collections */}
        <div className="mcm-scroll">
          {loading && (
            <div className="mcm-loading">
              <div className="mcm-spinner"></div>
              <p>Loading collections...</p>
            </div>
          )}

          {error && (
            <div className="mcm-error">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="#FF3B30" strokeWidth="2" />
                <path d="M10 6v4M10 14h.01" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && collections.map((collection, index) => (
            <button
              key={collection.collectionId}
              className={`mcm-item${collection.pendingAction ? ' mcm-item-pending' : ''}`}
              onClick={() => handleCheckboxClick(index)}
            >
              <div className="mcm-checkbox">
                {getCheckboxIcon(collection.state)}
              </div>
              <div className="mcm-info">
                <span className="mcm-name">
                  {collection.collectionName}
                  {collection.isSystem && <span className="mcm-badge">SYSTEM</span>}
                </span>
                <span className="mcm-count">
                  ({collection.currentCount}/{collection.totalCount})
                </span>
              </div>
              {collection.pendingAction && (
                <span className="mcm-pending">
                  {collection.pendingAction === 'add' ? '+ Adding' : '- Removing'}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mcm-footer">
          <button className="mcm-btn mcm-btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button 
            className="mcm-btn mcm-btn-apply" 
            onClick={handleApplyChanges}
            disabled={!hasPendingChanges || saving}
          >
            {saving ? 'Applying...' : 'Apply Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ManageCollectionsModal;
